/**
 * Smart Auto Scroll: the map from song time to scroll position.
 *
 * A constant scroll speed assumes a song spends the same amount of time on every pixel, and no song
 * does. An Intro is eight bars over one line of chords; a Chorus marked repeatCount 2 is the same
 * pixels sung twice; a Bridge is four dense lines over four bars. This module pairs each part's
 * musical duration (songTiming.js) with its measured height on screen and builds a schedule that
 * gives every part exactly as long as it takes to play.
 *
 * The schedule is built from what is actually rendered, so viewport height, font size, line
 * wrapping, Hide Chords and Overview are all accounted for by measurement rather than by rules.
 */

import {
  DEFAULT_SCROLL_SPEED,
  MAX_PLAUSIBLE_SONG_SECONDS,
  MAX_PX_PER_SEC,
  MIN_PLAUSIBLE_SONG_SECONDS,
  READING_FRACTION,
  speedToPxPerSecond,
} from './autoScrollConstants';
import { buildSongTimeline } from './songTiming';

const DEFAULT_PX_PER_SEC = speedToPxPerSecond(DEFAULT_SCROLL_SPEED);

/* ---------------------------------------------------------------------------
 * Monotone cubic interpolation (Fritsch-Carlson)
 *
 * The breakpoints alone describe a piecewise-linear path, which would change velocity abruptly at
 * every part boundary — a gear change the eye catches. A monotone cubic is smooth through the
 * boundaries and, unlike a plain spline, cannot overshoot: the scroll never creeps backwards
 * between two breakpoints.
 * ------------------------------------------------------------------------- */

const computeTangents = (xs, ys) => {
  const n = xs.length;
  if (n < 2) return [0];

  const h = [];
  const delta = [];
  for (let i = 0; i < n - 1; i += 1) {
    h.push(xs[i + 1] - xs[i]);
    delta.push(h[i] > 0 ? (ys[i + 1] - ys[i]) / h[i] : 0);
  }

  const m = new Array(n);
  m[0] = delta[0];
  m[n - 1] = delta[n - 2];

  for (let i = 1; i < n - 1; i += 1) {
    if (delta[i - 1] * delta[i] <= 0) {
      // A flat or a turn: a zero tangent is what keeps a hold from bulging into movement
      m[i] = 0;
    } else {
      const w1 = 2 * h[i] + h[i - 1];
      const w2 = h[i] + 2 * h[i - 1];
      m[i] = (w1 + w2) / (w1 / delta[i - 1] + w2 / delta[i]);
    }
  }

  return m;
};

const findSegment = (xs, x) => {
  let lo = 0;
  let hi = xs.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] <= x) lo = mid;
    else hi = mid;
  }
  return lo;
};

const hermite = (xs, ys, m, x, i) => {
  const h = xs[i + 1] - xs[i];
  if (h <= 0) return ys[i + 1];

  const u = (x - xs[i]) / h;
  const u2 = u * u;
  const u3 = u2 * u;

  return (
    ys[i] * (2 * u3 - 3 * u2 + 1) +
    h * m[i] * (u3 - 2 * u2 + u) +
    ys[i + 1] * (-2 * u3 + 3 * u2) +
    h * m[i + 1] * (u3 - u2)
  );
};

/* ---------------------------------------------------------------------------
 * Measurement
 * ------------------------------------------------------------------------- */

/**
 * The rendered parts, in document order. Reading them out of the DOM rather than off the song is
 * deliberate: ViewBody filters out overview-hidden parts and attached notes, and continuous view
 * renders several songs at once. Querying what was rendered inherits all of that for nothing.
 */
export const measureRenderedParts = () => {
  const nodes = document.querySelectorAll('#view-body [data-part-id]');
  if (!nodes.length) return [];

  const scrollY = window.scrollY;

  return Array.from(nodes).map((node) => {
    const rect = node.getBoundingClientRect();
    return {
      partId: node.dataset.partId,
      songId: node.dataset.songId,
      top: rect.top + scrollY,
      bottom: rect.bottom + scrollY,
    };
  });
};

/* ---------------------------------------------------------------------------
 * Schedule
 * ------------------------------------------------------------------------- */

/**
 * Scroll positions for each part, anchored so a part has reached the reading line by the time it
 * starts. Clamping the anchor at zero is what makes the opening of a song slow: while the navbar
 * and song header are still on screen the first parts are already visible, so there is nothing to
 * scroll yet and the page holds near the top through the intro.
 */
const buildAnchors = (measured, maxScroll, viewportHeight) => {
  const readingLine = viewportHeight * READING_FRACTION;

  // The first part starts at the top of the document — the reading line can't be honoured above
  // scroll position zero, and pretending otherwise would jump the page on the first frame.
  const anchors = measured.map((part, index) => (index === 0 ? 0 : Math.max(0, part.top - readingLine)));

  // One more for the end of the music, so the final part scrolls through itself rather than
  // sitting still while it plays
  anchors.push(Math.max(0, measured[measured.length - 1].bottom - readingLine));

  // Measurement noise and short parts can put an anchor behind its predecessor; the path has to
  // be monotone or the page would scroll backwards
  const flattened = anchors.map(() => false);
  for (let i = 1; i < anchors.length; i += 1) {
    if (anchors[i] < anchors[i - 1]) {
      anchors[i] = anchors[i - 1];
      // Worth reporting: an anchor pushed forward gives the part before it no pixels to cross, so
      // it reads on screen as a hold rather than as a scroll however long its estimate is
      flattened[i] = true;
    }
  }

  // The document may not scroll far enough to honour every anchor (a short page, a tall screen).
  // Compressing proportionally keeps the shape and lands the last part exactly at the bottom,
  // where clamping instead would leave a dead crawl at the end.
  const last = anchors[anchors.length - 1];
  const scale = last > maxScroll && last > 0 ? maxScroll / last : 1;

  return {
    // Clamped as well as scaled: the multiplication lands a hair over maxScroll in floating point,
    // and a target past the end would have the engine fighting the browser's own clamp every frame
    positions: anchors.map((anchor) => Math.min(anchor * scale, maxScroll)),
    flattened,
    scale,
    readingLine,
  };
};

/**
 * Durations per measured part, in seconds where the songs carry a BPM and in bars where they don't.
 * Continuous view mixes songs with different tempos, so each part is timed against its own song.
 */
const collectDurations = (measured, songs) => {
  const timelines = {};
  songs.forEach((song) => {
    if (song?._id) timelines[song._id] = { song, timeline: buildSongTimeline(song) };
  });

  // A single song may render without its id on the nodes; fall back to the only timeline there is
  const soleTimeline = songs.length === 1 ? timelines[songs[0]?._id] : null;

  let anyInSeconds = false;
  let allInSeconds = true;

  const durations = measured.map(({ partId, songId }) => {
    const entry = timelines[songId] || soleTimeline;
    const duration = entry?.timeline.durations[partId];

    if (!duration) return { seconds: null, bars: 1, measured: false };

    if (duration.seconds != null) anyInSeconds = true;
    else allInSeconds = false;

    return duration;
  });

  return { durations, unit: anyInSeconds && allInSeconds ? 'seconds' : 'bars' };
};

/**
 * Builds the time-to-position schedule for what is currently on screen.
 * Returns null when there is nothing to scroll (the song fits, or no parts are rendered).
 */
export const buildSchedule = (songs = []) => {
  const doc = document.documentElement;
  const maxScroll = doc.scrollHeight - doc.clientHeight;
  if (maxScroll <= 0) return null;

  const measured = measureRenderedParts();
  if (measured.length === 0) return null;

  const { positions, flattened, scale, readingLine } = buildAnchors(measured, maxScroll, doc.clientHeight);
  const { durations, unit } = collectDurations(measured, songs);

  // Bars when no BPM was available, seconds when it was. Either way these are only weights until
  // the total is settled below.
  const weights = durations.map((duration) =>
    Math.max(0, unit === 'seconds' ? duration.seconds : duration.bars || 0),
  );
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (totalWeight <= 0) return null;

  const distance = positions[positions.length - 1] - positions[0];

  // Without a BPM, or with a total that can't be right, the shape of the song still stands — only
  // its overall length is guessed. Pin that guess to the pace the fixed default speed would have
  // taken over the same distance, so smart scroll is never wildly off.
  const plausible =
    unit === 'seconds' && totalWeight >= MIN_PLAUSIBLE_SONG_SECONDS && totalWeight <= MAX_PLAUSIBLE_SONG_SECONDS;
  const totalSeconds = plausible ? totalWeight : Math.max(MIN_PLAUSIBLE_SONG_SECONDS, distance / DEFAULT_PX_PER_SEC);
  const secondsPerWeight = totalSeconds / totalWeight;

  // A part is only playing the length the chart gave it when a schedule second is a real second.
  // In bars mode there is no BPM to turn bars into seconds with, and when the total is implausible
  // the whole schedule is renormalised to a default velocity — either way every segment is running
  // at a length the scheduler chose, and none of them can claim to be measured.
  const honourMeasured = unit === 'seconds' && plausible;

  const times = [0];
  const capped = [];
  const measuredFlags = [];
  for (let i = 0; i < weights.length; i += 1) {
    const step = weights[i] * secondsPerWeight;
    const rise = positions[i + 1] - positions[i];
    const floor = rise / MAX_PX_PER_SEC;
    // No segment may outrun the cap, however short its estimate says it is. Lengthening the
    // segment rather than clipping the velocity keeps time and position in step.
    const isCapped = floor > step;
    capped.push(isCapped);
    // A capped segment has been lengthened past what the chart asked for, so it is the scheduler's
    // length now. A flattened one still holds for exactly the seconds the chart asked for.
    measuredFlags.push(honourMeasured && Boolean(durations[i]?.measured) && !isCapped);
    times.push(times[i] + Math.max(step, floor));
  }

  const tangents = computeTangents(times, positions);
  const duration = times[times.length - 1];

  const scrollAt = (t) => {
    if (t <= 0) return positions[0];
    if (t >= duration) return positions[positions.length - 1];
    return hermite(times, positions, tangents, t, findSegment(times, t));
  };

  return {
    duration,
    maxScroll,
    scrollAt,

    // What the song scrolls at on average, at pace 1. A reading against it is about the shape of
    // the song rather than about how fast it is being played — which is what makes it the right
    // thing to compare a part against, even though the pace multiplier no longer scales every
    // segment equally (see measuredAt).
    averageVelocity: distance / duration,

    /**
     * Whether the segment playing at time `t` was measured off the chart rather than estimated.
     *
     * The scroll engine's one input to the pace multiplier: a measured segment runs at the length
     * the chart gave it whatever the player has the slider set to, because there is no estimate
     * there for them to be correcting. Outside the song both ends read false — before it starts
     * and after it ends there is no part to be measured, and the engine is only ever advancing
     * towards one.
     */
    measuredAt: (t) => {
      if (!(t >= 0) || t >= duration) return false;
      return Boolean(measuredFlags[findSegment(times, t)]);
    },

    /** The part playing at time `t`, as rendered: `{ partId, songId }`, or null outside the song. */
    partAt: (t) => {
      if (!(t >= 0) || t >= duration) return null;
      const { partId, songId } = measured[findSegment(times, t)];
      return { partId, songId };
    },

    /**
     * Scroll speed at a moment, in px/s. Differentiated numerically rather than analytically:
     * the curve is a cubic whose derivative is easy enough to get wrong, and a central difference
     * over a twentieth of a second is exact enough for a three-way indicator.
     */
    velocityAt: (t) => {
      const h = 0.05;
      const from = Math.max(0, t - h);
      const to = Math.min(duration, t + h);
      return to > from ? (scrollAt(to) - scrollAt(from)) / (to - from) : 0;
    },

    /**
     * The inverse, for re-anchoring after the user scrolls by hand. Bisects the forward curve so
     * the two can't disagree. Where a position spans a hold, the latest time wins: someone who
     * drags forward is catching up, not asking to wait through the hold again.
     */
    timeAt: (y) => {
      if (y <= positions[0]) return 0;
      if (y >= positions[positions.length - 1]) return duration;

      let index = positions.length - 2;
      while (index > 0 && positions[index] > y) index -= 1;

      let lo = times[index];
      let hi = times[index + 1];
      for (let i = 0; i < 24; i += 1) {
        const mid = (lo + hi) / 2;
        if (scrollAt(mid) < y) lo = mid;
        else hi = mid;
      }
      return hi;
    },

    // Kept for inspection: `window.__smartScroll` in development
    breakpoints: times.map((time, i) => ({
      time,
      position: positions[i],
      partId: measured[i]?.partId ?? null,
      pxPerSecond: i < times.length - 1 ? (positions[i + 1] - positions[i]) / (times[i + 1] - times[i] || 1) : 0,
      // Why this segment came out the length it did — read by the Smart Scroll x-ray overlay, and
      // the difference between "the estimate asked for this" and "a guard rail overrode it"
      weight: weights[i] ?? 0,
      capped: Boolean(capped[i]),
      flattened: Boolean(flattened[i + 1]),
      // Whether the pace multiplier applies here — see measuredAt
      measured: Boolean(measuredFlags[i]),
    })),

    /**
     * The song-level decisions every part's pace is scaled by. Separate from the breakpoints
     * because they explain a whole schedule at once: which unit the weights are in, whether the
     * total was believed, and whether the page had room for the shape the anchors asked for.
     */
    diagnostics: {
      unit,
      plausible,
      honourMeasured,
      totalWeight,
      totalSeconds,
      secondsPerWeight,
      compression: scale,
      readingLine,
      distance,
    },
  };
};

/* ---------------------------------------------------------------------------
 * Registry
 *
 * The schedule is built by a hook in ViewBody (which owns the rendered DOM) and read by the scroll
 * engine in autoScrollHelpers, which is called from three places and has no access to the song.
 * A module-level slot keeps that seam narrow — no prop threading through three components.
 * ------------------------------------------------------------------------- */

let currentSchedule = null;

/**
 * Fired after every rebuild in a development build, so the x-ray overlay redraws against the
 * schedule that is actually installed rather than re-deriving the geometry on its own timer.
 */
export const SCHEDULE_CHANGED_EVENT = 'smartScrollScheduleChanged';

export const setSchedule = (schedule) => {
  currentSchedule = schedule;
  if (process.env.NODE_ENV === 'development') {
    window.__smartScroll = schedule;
    window.dispatchEvent(new Event(SCHEDULE_CHANGED_EVENT));
  }
};

export const getSchedule = () => currentSchedule;

export const clearSchedule = () => setSchedule(null);
